import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import Pagination from "../ui/pagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Loader2, Search, X, Eye, RefreshCw, Download, Upload, Check, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../lib/authContext";
import { STORAGE_KEY } from "../../lib/constants";

interface ProductListTabProps {
	quickFilter: string;
	onClearQuickFilter: () => void;
	onOpenProduct: (id: number) => void;
}

export default function ProductListTab({ quickFilter, onClearQuickFilter, onOpenProduct }: ProductListTabProps) {
	const [loading, setLoading] = useState(false);
	const [products, setProducts] = useState<any[]>([]);
	const [page, setPage] = useState(1);
	const [limit] = useState(20);
	const [total, setTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [search, setSearch] = useState("");
	const [mdmCategory, setMdmCategory] = useState("all");
	const [sortBy, setSortBy] = useState("id");
	const [sortOrder, setSortOrder] = useState("desc");
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [exporting, setExporting] = useState(false);
	const [importing, setImporting] = useState(false);
	const [importPreview, setImportPreview] = useState<any>(null);
	const [showImportDialog, setShowImportDialog] = useState(false);
	const [applyingImport, setApplyingImport] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const abortRef = useRef<AbortController | null>(null);
	const { toast } = useToast();
	const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

	const getUserInfo = () => {
		const user = localStorage.getItem(STORAGE_KEY);
		if (!user) return { email: "unknown@pillplus.com" };
		const parsed = JSON.parse(user);
		return { email: parsed.email || "unknown@pillplus.com", name: parsed.displayName || parsed.name };
	};

	const fetchProducts = useCallback(async () => {
		setLoading(true);
		try {
			const params: Record<string, any> = {
				page,
				limit,
				sort_by: sortBy,
				sort_order: sortOrder,
			};
			if (search.trim()) params.search = search.trim();
			if (mdmCategory && mdmCategory !== "all") params.mdm_category = mdmCategory;
			if (quickFilter) params.quick_filter = quickFilter;

			const res = await api.b2cMgmtGetProducts(params);
			if (res?.success) {
				setProducts(res.data || []);
				setTotal(res.pagination?.total || 0);
				setTotalPages(res.pagination?.totalPages || 0);
			} else {
				toast({ title: "Error", description: res?.error || "Failed to fetch products", variant: "destructive" });
			}
		} catch (error: any) {
			console.error("Error fetching products:", error);
		} finally {
			setLoading(false);
		}
	}, [page, limit, search, mdmCategory, sortBy, sortOrder, quickFilter]);

	useEffect(() => {
		fetchProducts();
	}, [fetchProducts]);

	useEffect(() => {
		setPage(1);
	}, [search, mdmCategory, quickFilter]);

	const handleSearchChange = (value: string) => {
		if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		searchTimerRef.current = setTimeout(() => {
			setSearch(value);
		}, 400);
	};

	const handleToggleFlag = async (productId: number, flag: string, currentValue: boolean) => {
		const user = getUserInfo();
		try {
			const res = await api.b2cMgmtUpdateFlags(productId, { [flag]: !currentValue }, user);
			if (res?.success) {
				setProducts((prev) =>
					prev.map((p) => (p.id === productId ? { ...p, [flag]: !currentValue } : p)),
				);
				toast({ title: "Updated", description: res.message });
			} else {
				toast({ title: "Error", description: res?.error, variant: "destructive" });
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to update flag", variant: "destructive" });
		}
	};

	const toggleSelectAll = () => {
		if (selectedIds.length === products.length) {
			setSelectedIds([]);
		} else {
			setSelectedIds(products.map((p) => p.id));
		}
	};

	const toggleSelect = (id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
		);
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const response = await api.b2cMgmtExportProducts();
			if (response?.data) {
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const a = document.createElement("a");
				a.href = url;
				a.download = response.headers?.["content-disposition"]?.split("filename=")?.[1]?.replace(/"/g, "")
					|| `med_products_export_${new Date().toISOString().split("T")[0]}.xlsx`;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
				toast({ title: "Exported", description: "Excel file downloaded" });
			}
		} catch (error: any) {
			toast({ title: "Export failed", description: error?.message || "Failed to export", variant: "destructive" });
		} finally {
			setExporting(false);
		}
	};

	const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setImporting(true);
		try {
			const reader = new FileReader();
			reader.onload = async (event) => {
				const base64 = (event.target?.result as string).split(",")[1];
				const res = await api.b2cMgmtImportPreview(base64);
				if (res?.success) {
					setImportPreview(res.data);
					setShowImportDialog(true);
				} else {
					toast({ title: "Import failed", description: res?.error || "Failed to parse file", variant: "destructive" });
				}
				setImporting(false);
			};
			reader.readAsDataURL(file);
		} catch (error: any) {
			toast({ title: "Import failed", description: error?.message, variant: "destructive" });
			setImporting(false);
		}
		// Reset file input
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleApplyImport = async () => {
		if (!importPreview?.changes?.length) return;
		setApplyingImport(true);
		try {
			const user = getUserInfo();
			const res = await api.b2cMgmtImportApply({ changes: importPreview.changes, user });
			if (res?.success) {
				const d = res.data;
				const parts = [`${d.applied} products updated`];
				if (d.failed) parts.push(`${d.failed} failed`);
				if (d.typesense_synced) parts.push(`${d.typesense_synced} synced to search`);
				toast({ title: "Import complete", description: parts.join(", ") });

				// If Typesense sync has pending items, auto-retry after 5s
				if (d.typesense_pending > 0) {
					toast({ title: "Search sync pending", description: `${d.typesense_pending} products pending search sync. Retrying...`, variant: "destructive" });
					setTimeout(async () => {
						try {
							const retry = await api.b2cMgmtRetryTypesenseSync();
							if (retry?.success && retry.data.synced > 0) {
								toast({ title: "Search sync complete", description: `${retry.data.synced} products synced to search` });
							}
						} catch {}
					}, 5000);
				}

				setShowImportDialog(false);
				setImportPreview(null);
				fetchProducts();
			} else {
				toast({ title: "Import failed", description: res?.error, variant: "destructive" });
			}
		} catch (error: any) {
			toast({ title: "Import failed", description: error?.message, variant: "destructive" });
		} finally {
			setApplyingImport(false);
		}
	};

	const mdmCategoryBadgeColor = (cat: string | null) => {
		if (!cat) return "secondary";
		switch (cat) {
			case "Rx": return "default";
			case "Generic": return "outline";
			case "FMCG": return "secondary";
			case "DPCO": return "destructive";
			default: return "secondary";
		}
	};

	return (
		<div className="space-y-4">
			{/* Filters */}
			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-4">
					<div className="flex flex-wrap items-center gap-3">
						<div className="relative flex-1 min-w-[200px]">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
							<Input
								placeholder="Search by name, code, company..."
								className="pl-9 dark:bg-gray-700 dark:border-gray-600"
								onChange={(e) => handleSearchChange(e.target.value)}
							/>
						</div>
						<Select value={mdmCategory} onValueChange={setMdmCategory}>
							<SelectTrigger className="w-[180px] dark:bg-gray-700 dark:border-gray-600">
								<SelectValue placeholder="MDM Category" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Categories</SelectItem>
								<SelectItem value="Rx">Rx</SelectItem>
								<SelectItem value="Generic">Generic</SelectItem>
								<SelectItem value="FMCG">FMCG</SelectItem>
								<SelectItem value="DPCO">DPCO</SelectItem>
								<SelectItem value="Rx+Free">Rx+Free</SelectItem>
								<SelectItem value="uncategorized">Uncategorized</SelectItem>
							</SelectContent>
						</Select>
						<Select value={sortBy} onValueChange={setSortBy}>
							<SelectTrigger className="w-[140px] dark:bg-gray-700 dark:border-gray-600">
								<SelectValue placeholder="Sort by" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="id">ID</SelectItem>
								<SelectItem value="product_name">Name</SelectItem>
								<SelectItem value="mrp">MRP</SelectItem>
								<SelectItem value="updated_at">Updated</SelectItem>
							</SelectContent>
						</Select>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
							className="dark:bg-gray-700 dark:border-gray-600"
						>
							{sortOrder === "asc" ? "↑" : "↓"}
						</Button>
						{quickFilter && (
							<Badge variant="destructive" className="cursor-pointer" onClick={onClearQuickFilter}>
								Filter: {quickFilter} <X className="h-3 w-3 ml-1" />
							</Badge>
						)}
						<Button variant="outline" size="sm" onClick={fetchProducts} className="dark:bg-gray-700 dark:border-gray-600">
							<RefreshCw className="h-4 w-4" />
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={handleExport}
							disabled={exporting}
							className="dark:bg-gray-700 dark:border-gray-600"
						>
							{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
							<span className="ml-1 hidden sm:inline">Export</span>
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => fileInputRef.current?.click()}
							disabled={importing}
							className="dark:bg-gray-700 dark:border-gray-600"
						>
							{importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
							<span className="ml-1 hidden sm:inline">Import</span>
						</Button>
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx,.xls"
							className="hidden"
							onChange={handleImportFile}
						/>
					</div>
					<div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
						{total.toLocaleString()} products found
						{selectedIds.length > 0 && ` — ${selectedIds.length} selected`}
					</div>
				</CardContent>
			</Card>

			{/* Table */}
			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-0">
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
									<th className="py-3 px-3 text-left">
										<input
											type="checkbox"
											checked={selectedIds.length === products.length && products.length > 0}
											onChange={toggleSelectAll}
											className="rounded"
										/>
									</th>
									<th className="py-3 px-3 text-left dark:text-gray-300">ID</th>
									<th className="py-3 px-3 text-left dark:text-gray-300">Product Name</th>
									<th className="py-3 px-3 text-left dark:text-gray-300">MDM Cat</th>
									<th className="py-3 px-3 text-left dark:text-gray-300">Packing</th>
									<th className="py-3 px-3 text-right dark:text-gray-300">MRP</th>
									<th className="py-3 px-3 text-right dark:text-gray-300">Price</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">Active</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">Drug</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">Rx</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">Disc</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">Banned</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">NFS</th>
									<th className="py-3 px-3 text-center dark:text-gray-300">Actions</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan={14} className="text-center py-12">
											<Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-500" />
										</td>
									</tr>
								) : products.length === 0 ? (
									<tr>
										<td colSpan={14} className="text-center py-12 text-gray-500 dark:text-gray-400">
											No products found
										</td>
									</tr>
								) : (
									products.map((product) => (
										<tr
											key={product.id}
											className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50"
										>
											<td className="py-2 px-3">
												<input
													type="checkbox"
													checked={selectedIds.includes(product.id)}
													onChange={() => toggleSelect(product.id)}
													className="rounded"
												/>
											</td>
											<td className="py-2 px-3 font-mono text-xs dark:text-gray-300">{product.id}</td>
											<td className="py-2 px-3 max-w-[250px]">
												<div className="truncate dark:text-gray-200" title={product.product_name}>
													{product.product_name}
												</div>
												<div className="text-xs text-gray-400">{product.company_name}</div>
											</td>
											<td className="py-2 px-3">
												{product.mdm_category ? (
													<Badge variant={mdmCategoryBadgeColor(product.mdm_category)} className="text-xs">
														{product.mdm_category}
													</Badge>
												) : (
													<span className="text-xs text-red-400">—</span>
												)}
											</td>
											<td className="py-2 px-3 text-xs dark:text-gray-300 max-w-[100px] truncate">
												{product.packing || "—"}
											</td>
											<td className="py-2 px-3 text-right font-mono dark:text-gray-200">
												{product.mrp ? `₹${Number(product.mrp).toFixed(2)}` : "—"}
											</td>
											<td className="py-2 px-3 text-right font-mono dark:text-gray-200">
												{product.current_price ? `₹${Number(product.current_price).toFixed(2)}` : "—"}
												{product.mrp && product.current_price && Number(product.current_price) < Number(product.mrp) && (
													<span className="text-xs text-green-500 block">
														-{(((Number(product.mrp) - Number(product.current_price)) / Number(product.mrp)) * 100).toFixed(0)}%
													</span>
												)}
											</td>
											<td className="py-2 px-3 text-center">
												<Switch
													checked={product.is_active}
													onCheckedChange={() => handleToggleFlag(product.id, "is_active", product.is_active)}
												/>
											</td>
											<td className="py-2 px-3 text-center">
												<Switch
													checked={product.is_drug}
													onCheckedChange={() => handleToggleFlag(product.id, "is_drug", product.is_drug)}
												/>
											</td>
											<td className="py-2 px-3 text-center">
												<Switch
													checked={product.prescription_required}
													onCheckedChange={() => handleToggleFlag(product.id, "prescription_required", product.prescription_required)}
												/>
											</td>
											<td className="py-2 px-3 text-center">
												<Switch
													checked={product.is_discount_applicable}
													onCheckedChange={() => handleToggleFlag(product.id, "is_discount_applicable", product.is_discount_applicable)}
												/>
											</td>
											<td className="py-2 px-3 text-center">
												<Switch
													checked={product.banned_for_sale}
													onCheckedChange={() => handleToggleFlag(product.id, "banned_for_sale", product.banned_for_sale)}
												/>
											</td>
											<td className="py-2 px-3 text-center">
												<Switch
													checked={product.not_for_sale}
													onCheckedChange={() => handleToggleFlag(product.id, "not_for_sale", product.not_for_sale)}
												/>
											</td>
											<td className="py-2 px-3 text-center">
												<Button
													variant="ghost"
													size="sm"
													onClick={() => onOpenProduct(product.id)}
													title="View Details"
												>
													<Eye className="h-4 w-4" />
												</Button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex justify-center">
					<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
				</div>
			)}

			{/* Import Preview Dialog */}
			<Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
				<DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col dark:bg-gray-800">
					<DialogHeader>
						<DialogTitle className="dark:text-gray-100">Import Preview — Changes Detected</DialogTitle>
					</DialogHeader>

					{importPreview && (
						<div className="flex-1 overflow-hidden flex flex-col">
							{/* Summary */}
							<div className="flex gap-4 mb-4 text-sm">
								<div className="px-3 py-2 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
									<span className="font-semibold">{importPreview.total_rows}</span> rows in file
								</div>
								<div className="px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
									<AlertTriangle className="h-4 w-4 inline mr-1" />
									<span className="font-semibold">{importPreview.total_changes}</span> products with changes
								</div>
								<div className="px-3 py-2 rounded bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300">
									<Check className="h-4 w-4 inline mr-1" />
									<span className="font-semibold">{importPreview.unchanged}</span> unchanged
								</div>
								{importPreview.not_found > 0 && (
									<div className="px-3 py-2 rounded bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
										<span className="font-semibold">{importPreview.not_found}</span> not found in DB
									</div>
								)}
							</div>

							{importPreview.truncated && (
								<div className="text-xs text-amber-500 mb-2">
									Showing first 500 of {importPreview.total_changes} changes
								</div>
							)}

							{/* Changes table */}
							<div className="flex-1 overflow-auto border rounded dark:border-gray-700">
								<table className="w-full text-xs">
									<thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
										<tr>
											<th className="py-2 px-3 text-left dark:text-gray-300">ID</th>
											<th className="py-2 px-3 text-left dark:text-gray-300">Product Name</th>
											<th className="py-2 px-3 text-left dark:text-gray-300">Field</th>
											<th className="py-2 px-3 text-left dark:text-gray-300">Current Value</th>
											<th className="py-2 px-3 text-left dark:text-gray-300">New Value</th>
										</tr>
									</thead>
									<tbody>
										{importPreview.changes?.map((change: any, ci: number) =>
											change.fields.map((f: any, fi: number) => (
												<tr
													key={`${ci}-${fi}`}
													className="border-t dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
												>
													{fi === 0 && (
														<>
															<td rowSpan={change.fields.length} className="py-1.5 px-3 font-mono text-gray-500 dark:text-gray-400 align-top border-r dark:border-gray-700/50">
																{change.id}
															</td>
															<td rowSpan={change.fields.length} className="py-1.5 px-3 dark:text-gray-200 align-top max-w-[200px] truncate border-r dark:border-gray-700/50">
																{change.product_name}
															</td>
														</>
													)}
													<td className="py-1.5 px-3 font-mono text-blue-600 dark:text-blue-400">
														{f.header}
													</td>
													<td className="py-1.5 px-3 text-red-500 dark:text-red-400 max-w-[150px] truncate" title={String(f.old_value ?? "")}>
														{f.old_value !== null && f.old_value !== undefined && f.old_value !== "" ? String(f.old_value) : "—"}
													</td>
													<td className="py-1.5 px-3 text-green-600 dark:text-green-400 max-w-[150px] truncate" title={String(f.new_value ?? "")}>
														{f.new_value !== null && f.new_value !== undefined && f.new_value !== "" ? String(f.new_value) : "—"}
													</td>
												</tr>
											)),
										)}
									</tbody>
								</table>
								{importPreview.total_changes === 0 && (
									<div className="text-center py-8 text-gray-500">
										No changes detected — the file matches the current database.
									</div>
								)}
							</div>
						</div>
					)}

					<DialogFooter className="mt-4">
						<Button variant="outline" onClick={() => { setShowImportDialog(false); setImportPreview(null); }}>
							Cancel
						</Button>
						<Button
							onClick={handleApplyImport}
							disabled={applyingImport || !importPreview?.total_changes}
							className="bg-blue-600 hover:bg-blue-700 text-white"
						>
							{applyingImport ? (
								<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Applying...</>
							) : (
								<>Apply {importPreview?.total_changes || 0} Changes</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
